import React, { useCallback, useMemo, useState } from 'react'
import { Button, Form, message, Modal } from 'antd'
import { EditOutlined } from '@ant-design/icons'
import { useSelector } from 'react-redux'
import { useIntl } from 'react-intl'
import { useMutation } from 'react-query'

import { openNewTab } from 'utils/helper'
import Cancel from 'components/UI/Button/Cancel'
import Send from 'components/UI/Button/Send'
import HideFeature from 'containers/Billing/HideFeature'
import WhatsappLimitModal, { getWhatsappLimitMessage } from 'containers/Billing/WhatsappLimitModal'
import FormTab from 'components/UI/SendWaModal/FormTab'
import {
  sendFinanceInvoiceWhatsapp,
  sendFinanceOrderWhatsapp,
  sendFinancePurchaseInvoiceWhatsapp,
  sendFinancePurchaseOrderWhatsapp,
  sendFinancePurchaseQuoteWhatsapp,
  sendFinanceQuoteWhatsapp,
} from 'utils/apis'
import { transType } from 'utils/static'
import { billingSelector, isFinanceOnboardingSelector } from 'app-redux/selectors'
import ModalWhatsappAddon from 'components/apps/EmailTemplate/ModalWhatsappAddon'
import { formatWhatsappPhone, replaceWhatsappBody } from '@/utils/helper'

const useSendWhatsappMutation = ({ transTypeId }) => {
  const fetchFunction = useMemo(() => {
    switch (transTypeId) {
      case transType.INVOICE:
        return sendFinanceInvoiceWhatsapp
      case transType.ORDER:
        return sendFinanceOrderWhatsapp
      case transType.QUOTE:
        return sendFinanceQuoteWhatsapp
      case transType.PURCHASE_INVOICE:
        return sendFinancePurchaseInvoiceWhatsapp
      case transType.PURCHASE_ORDER:
        return sendFinancePurchaseOrderWhatsapp
      case transType.PURCHASE_QUOTE:
        return sendFinancePurchaseQuoteWhatsapp
      default:
        return sendFinanceInvoiceWhatsapp
    }
  }, [transTypeId])

  return useMutation(fetchFunction)
}

const SendWaModal = ({
  sendTitle,
  getWaTemplate,
  show = false,
  onCloseWaModal,
  goToWaTamplate,
  hideOfficial,
  id,
  transTypeId,
  waTemplateKey,
}) => {
  const [showBillingWhatsappModal, setShowBillingWhatsappModal] = useState(false)
  const [whatsappType, setWhatsappType] = useState(1)
  const [whatsappLimitMessage, setWhatsappLimitMessage] = useState('')

  const [form] = Form.useForm()

  const { formatMessage } = useIntl()

  const billing = useSelector(billingSelector)
  const isFinanceOnboarding = useSelector(isFinanceOnboardingSelector)

  const sendWhatsappMutation = useSendWhatsappMutation({ transTypeId })

  const onSendWa = useCallback(async () => {
    // Dapatkan form values dengan preserve = true. Field yang tidak di register harus ikut
    const values = form.getFieldsValue(true)

    if (whatsappType === 2) {
      if (billing.max_whatsapp === 0) {
        setShowBillingWhatsappModal(true)
        return
      }

      sendWhatsappMutation.mutate(
        { id, payload: values.official_whatsapp_value_replacement },
        {
          onSuccess: (response) => {
            message.success(response.data.message)
            onCloseWaModal()
          },
          onError: (response) => {
            message.error(response.data.message)
            const limitMessage = getWhatsappLimitMessage(response)
            if (limitMessage) {
              setWhatsappLimitMessage(limitMessage)
            }
          },
        },
      )
      return
    }

    const body = replaceWhatsappBody(values.body)
    const phone = formatWhatsappPhone(values.phone_number)
    openNewTab(`https://api.whatsapp.com/send?phone=${phone}&text=${body}`, false)
    onCloseWaModal()
  }, [billing.max_whatsapp, form, id, onCloseWaModal, sendWhatsappMutation, whatsappType])

  const onCloseBillingWhatsappModal = useCallback(() => setShowBillingWhatsappModal(false), [])

  const onCloseWhatsappLimitModal = useCallback(() => setWhatsappLimitMessage(''), [])

  const footer = useMemo(() => {
    const footerItems = []
    if (whatsappType === 1 && typeof goToWaTamplate === 'function') {
      footerItems.push(
        <Button
          key="template"
          style={{ float: 'left' }}
          icon={<EditOutlined />}
          onClick={goToWaTamplate}
        >
          {formatMessage({ id: 'wa.change_template' })}
        </Button>,
      )
    }
    footerItems.push(
      <Cancel key={`_${0}`} onClick={onCloseWaModal} />,
      <Send
        form="sendWaForm"
        key="submit"
        htmlType="submit"
        loading={sendWhatsappMutation.isLoading}
      />,
    )

    return footerItems
  }, [whatsappType, goToWaTamplate, formatMessage, onCloseWaModal, sendWhatsappMutation])

  return (
    <>
      <Modal
        title={
          sendTitle === 'button.send_wa' || !sendTitle
            ? formatMessage({ id: 'button.send_wa' })
            : sendTitle
        }
        open={show}
        onCancel={onCloseWaModal}
        destroyOnHidden
        footer={footer}
        width={whatsappType === 2 ? 700 : undefined}
      >
        {/* Onboarding pertama kali: kirim WA adalah langkah wajib panduan, jadi gate paket
            WhatsApp dilewati selama panduan aktif. Di luar itu tetap berlaku seperti biasa. */}
        <HideFeature
          code="whatsapp"
          feature={
            sendTitle === 'button.send_wa' || !sendTitle
              ? formatMessage({ id: 'button.send_wa' })
              : sendTitle
          }
          forceHiddenFeature={isFinanceOnboarding}
        >
          <Form
            form={form}
            onFinish={onSendWa}
            layout="vertical"
            id="sendWaForm"
            initialValues={{
              phone_number: '',
              body: '',
              official_whatsapp_value_replacement: [],
            }}
          >
            <FormTab
              getWaTemplate={getWaTemplate}
              hideOfficial={hideOfficial}
              id={id}
              show={show}
              transTypeId={transTypeId}
              waTemplateKey={waTemplateKey}
              onChangeWhatsappType={setWhatsappType}
              whatsappType={whatsappType}
            />
          </Form>
        </HideFeature>
      </Modal>
      {showBillingWhatsappModal && (
        <ModalWhatsappAddon
          description={formatMessage({ id: 'whatsappModal.out_of_whatsapp_balance_desc' })}
          open={showBillingWhatsappModal}
          onClose={onCloseBillingWhatsappModal}
        />
      )}
      <WhatsappLimitModal message={whatsappLimitMessage} onClose={onCloseWhatsappLimitModal} />
    </>
  )
}

export default SendWaModal
